import { useEffect, useState } from 'react';
import parsehtml from 'html-react-parser';
import { isAttribute } from '../../../helpers/is-attribute';
import { isSearchingForProducts } from '../../../helpers/is-searching-for-products';
import { ObjectTypeSelector } from '../../../components/ObjectTypeSelector';
import { useObjectTypeSelector } from '../../../hooks/useObjectTypeSelector';
import { setupColumns } from '../helpers/setupColumns';
import ViewChange from '../../../components/ViewChange';

const ResultsBlock = ( { attributes } ) => {
	const {
		objectType,
		template,
		itemPerPage,
		showEmptyQueryPlaceholder,
		additionalPostTypesToShow = [],
		showVariationsOnlyForAttributes,
		disablePersonalization,
		showObjectTypeSelector,
		overrideTemplates,
		showFancyAnimations,
		promoteFeaturedContent,
		searchableTaxonomies = [],
		showViewPicker,
	} = attributes;
	const [ results, setResults ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ isEmptyQuery, setIsEmptyQuery ] = useState( false );

	const [ columns, setColumns ] = useState( () => attributes.columns );
	const [ mobileColumns, setMobileColumns ] = useState( () => attributes.mobileColumns );

	const isSearchingProducts = isSearchingForProducts( [ objectType, ...( additionalPostTypesToShow || [] ) ] );

	useEffect( () => {
		wp.hooks.addFilter( 'qalaElasticCore.filter.isPersonalizationEnabled', 'qalaElasticCoreResultsBlock', () => ! disablePersonalization );
		wp.hooks.addFilter( 'qalaElasticCore.filter.isCustomDisjunctiveSearch', 'qalaElasticCoreResultsBlock', () => isSearchingProducts && showVariationsOnlyForAttributes );
	}, [] );

	/**
	 * A more complete setter for isEmptyQuery that allows other plugins to interact with it.
	 *
	 * @param {boolean} value Value of isEmptyQuery
	 */
	const setIsEmptyQueryFull = value => {
		const filteredValue = wp.hooks.applyFilters( 'qalaElasticCore.filter.isEmptyQuery', value, showEmptyQueryPlaceholder );
		setIsEmptyQuery( filteredValue );
		QalaElasticCore.isEmptyQuery = filteredValue;
		QalaElasticCore.showEmptyQueryPlaceholder = showEmptyQueryPlaceholder;
	};

	/**
	 * Default filters for a query.
	 */
	const defaultFilters = [
		{ blog_id: ( window?.QalaElasticCore?.currentBlogId || 1 ).toString() },
		{ object_type: ( attributes.additionalPostTypesToShow && attributes.additionalPostTypesToShow.length > 0 ) ? [ objectType, ...attributes.additionalPostTypesToShow ] : objectType },
		{ is_visible: 'true' },
	];

	/**
	 * Default filters for a query.
	 */
	const defaultFiltersNone = [
		{ object_type: showVariationsOnlyForAttributes ? 'product_variation' : '' },
	];

	const addLoadingClass = () => {
		const loadedClass = 'qec-is-loading';
		const body = document.querySelector( 'body' );

		if ( ! body.classList.contains( loadedClass ) ) {
			body.classList.add( loadedClass );
		}
	};

	const addLoadedClass = () => {
		const loadedClass = 'qec-loaded';
		const body = document.querySelector( 'body' );

		if ( ! body.classList.contains( loadedClass ) ) {
			body.classList.add( loadedClass );
		}
	};

	/**
	 * useEffect
	 *
	 * using wp hooks to set query, do query, and set the results.
	 */
	useEffect( () => {
		addLoadingClass();

		// filter query should be before doQuery doAction.
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock', function( query ) {
			// Set the initial query for initial search.
			// We need to explicitly check for query.query being null here, otherwise each request will default to window.QalaElasticCore.query
			// regardless of search input fields's values. If we just check if it's empty, it means that clearing an input field (which means
			// query.query == "" will change the query to window.QalaElasticCore.query, which is the value in ?s URL param)
			if ( query.query === null && window.QalaElasticCore.query ) {
				query.query = window.QalaElasticCore.query;
			} else if ( ! query.query ) { // This just makes sure we don't have any type issues.
				query.query = '';
			}

			query.options = {
				page: {
					size: itemPerPage,
				},
				filters: { all: [ ...defaultFilters ] },
			};

			if ( ! query.options?.sort && promoteFeaturedContent ) {
				query.options.sort = [ { is_featured: 'desc' } ];
			} else if ( promoteFeaturedContent ) {
				if ( ! Array.isArray( query.options.sort ) ) {
					query.options.sort = [ query.options.sort ];
				}
				query.options.sort = [ { is_featured: 'desc' }, ...query.options.sort ];
			}

			return query;
		}, 1 );

		// Check query after all filters / facets have finished doing their thing if we need to show placeholder instead of results
		// for empty query (i.e. query without any filters or search query). This needs to run AFTER all plugin / package code
		// but before any user-land code.
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_EmptyQuery', function( query ) {
			if ( query.query ) {
				setIsEmptyQueryFull( false );
				return query;
			}

			const filters = query.options.filters;

			if ( filters.any || filters.none || filters.all.length > defaultFilters.length ) {
				setIsEmptyQueryFull( false );
				return query;
			}

			setIsEmptyQueryFull( true );
			return query;
		}, 99 );

		// Check if we need to show variations only for attributes.
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_Variations', function( query ) {
			if ( ! isSearchingProducts || ! showVariationsOnlyForAttributes ) {
				return query;
			}

			// Check if there's any attribute filters in current query.
			const hasAnyAttributeFilters = [ ...( query.options.filters?.all || [] ), ...( query.options.filters?.any || [] ) ].some( filter => {
				const filterKey = Object.keys( filter )[ 0 ];
				return isAttribute( filterKey );
			} );

			if ( hasAnyAttributeFilters ) {
				return query;
			}

			// Exclude product variations from the query (since the query doesn't filter by any attributes).
			const noneFilters = query.options.filters?.none;

			if ( ! noneFilters ) {
				query.options.filters.none = [ ...defaultFiltersNone ];
			} else {
				query.options.filters.none = [ ...noneFilters, ...defaultFiltersNone ];
			}

			query.options.showVariationsOnlyForAttributes = showVariationsOnlyForAttributes;

			return query;
		}, 110 );

		// We need to call doQuery with query param as null to correctly set the default query if needed but only on the initial load.
		wp.hooks.doAction( 'qalaElasticCore.action.doQuery', { query: null, options: {} } );
		wp.hooks.addAction( 'qalaElasticCore.action.onResult.results', 'qalaElasticCore', elasticResults => {
			if ( ! showFancyAnimations || ! document.startViewTransition ) {
				setResults( elasticResults );
			} else {
				document.startViewTransition( () => {
					setResults( elasticResults );
				} );
			}
			setLoading( false );
			addLoadedClass();
		} );
	}, [] );

	const {
		allObjectTypes,
		searchOnClick,
		specificObjectTypeTemplate,
	} = useObjectTypeSelector( {
		objectType,
		showObjectTypeSelector,
		template,
		overrideTemplates,
		usedObjectTypes: [
			...( additionalPostTypesToShow || [] ),
			... ( searchableTaxonomies || [] ),
			objectType,
		],
	} );

	if ( loading ) {
		return parsehtml( wp.template( 'qec-loading' )( { attributes } ) );
	}

	if ( ! results.length ) {
		return parsehtml( wp.template( 'qec-no-results' )( { attributes } ) );
	}

	if ( showEmptyQueryPlaceholder && isEmptyQuery ) {
		return parsehtml( wp.template( 'qec-empty-query-placeholder' )( { attributes } ) );
	}

	return (
		<>
			{ showObjectTypeSelector && <ObjectTypeSelector allObjectTypes={ allObjectTypes } searchOnClick={ searchOnClick } /> }
			{ showViewPicker &&
			<ViewChange
				attributes={ attributes }
				setColumns={ setColumns }
				setMobileColumns={ setMobileColumns }
			/>
			}
			{ parsehtml( wp.template( specificObjectTypeTemplate )( { results, attributes: setupColumns( attributes, columns, mobileColumns ) } ) ) }
		</>
	);
};

export default ResultsBlock;
